import {
  FC,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import classes from './index.module.css'

const iconProps = {
  onPointerEnterCapture: undefined,
  onPointerLeaveCapture: undefined,
}

const DownIcon = DownOutlined as unknown as FC<
  typeof iconProps & { className?: string }
>
const UpIcon = UpOutlined as unknown as FC<
  typeof iconProps & { className?: string }
>

export type ExpandableTextPanelTone =
  | 'default'
  | 'comment'
  | 'success'
  | 'rejected'

export type ExpandableTextPanelAppearance = 'default' | 'plain'

export type ExpandableTextPanelProps = {
  title: string
  value: string
  collapsible?: boolean
  tone?: ExpandableTextPanelTone
  appearance?: ExpandableTextPanelAppearance
}

const COLLAPSED_LINES = 3

const ExpandableTextPanel: FC<ExpandableTextPanelProps> = ({
  title,
  value,
  collapsible = false,
  tone = 'default',
  appearance = 'default',
}) => {
  const [expanded, setExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  const measureOverflow = useCallback(() => {
    const element = textRef.current
    if (!element || !collapsible || expanded) return

    setHasOverflow(element.scrollHeight > element.clientHeight + 1)
  }, [collapsible, expanded])

  useLayoutEffect(() => {
    if (!collapsible || !value) {
      setHasOverflow(false)
      return
    }

    if (expanded) return

    measureOverflow()
  }, [collapsible, value, expanded, measureOverflow])

  useLayoutEffect(() => {
    if (!collapsible || expanded || !textRef.current) return

    const element = textRef.current
    const resizeObserver = new ResizeObserver(() => {
      measureOverflow()
    })

    resizeObserver.observe(element)
    window.addEventListener('resize', measureOverflow)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measureOverflow)
    }
  }, [collapsible, expanded, measureOverflow])

  if (!value) return null

  const isPlain = appearance === 'plain'
  const isCollapsed = collapsible && !expanded && hasOverflow
  const showToggle = collapsible && hasOverflow

  return (
    <div
      className={classNames(
        classes.panel,
        tone === 'comment' && classes.panel_comment,
        tone === 'success' && classes.panel_success,
        tone === 'rejected' && classes.panel_rejected,
        isPlain && classes.panel_plain,
        isPlain && tone === 'rejected' && classes.panel_plain_rejected,
        isPlain && tone === 'success' && classes.panel_plain_success,
      )}
    >
      {title.trim() ? (
        <p
          className={classNames(
            classes.panelLabel,
            isPlain && classes.panelLabel_plain,
          )}
        >
          {title}
        </p>
      ) : null}

      <div
        className={classNames(
          classes.panelBodyWrap,
          isCollapsed && classes.panelBodyWrap_collapsed,
        )}
        style={
          collapsible && !expanded
            ? ({ '--collapsed-lines': COLLAPSED_LINES } as CSSProperties)
            : undefined
        }
      >
        <p
          ref={textRef}
          className={classNames(
            classes.panelBody,
            collapsible &&
              !expanded &&
              (hasOverflow ? classes.panelBody_clamped : classes.panelBody_measure),
          )}
        >
          {value}
        </p>
        {isCollapsed ? (
          <div
            className={classNames(
              classes.panelFade,
              isPlain && classes.panelFade_plain,
            )}
            aria-hidden
          />
        ) : null}
      </div>

      {showToggle ? (
        <button
          type="button"
          className={classes.expandToggle}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          <span>{expanded ? 'Свернуть' : 'Читать далее'}</span>
          {expanded ? (
            <UpIcon className={classes.expandToggleIcon} {...iconProps} />
          ) : (
            <DownIcon className={classes.expandToggleIcon} {...iconProps} />
          )}
        </button>
      ) : null}
    </div>
  )
}

export default ExpandableTextPanel
